import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  CoverageChips,
  LabsSection,
  SiteHeader,
  WorkingSample,
  teacherSiteUrl,
  useFrontDoorFonts,
  useLandingTelemetry,
} from '@shared-ui/front-door';

/** Schools are sales-led (front-door-target.md): a contact, not a sign-up. */
export const SCHOOLS_CONTACT = 'mailto:support@buzzbrain.ai?subject=BuzzBrain%20for%20our%20school';

function Tick() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

/**
 * The hub at `buzzbrain.ai` — tells anyone in five seconds what BuzzBrain is
 * and sends them through the right door: teachers to the teacher site,
 * students to sign in. See docs/FeatureDesigns/front-door/front-door-target.md,
 * "Page 1".
 */
export default function LandingHub() {
  const { isAuthenticated, isLoading } = useAuth();
  const track = useLandingTelemetry('hub', isAuthenticated, isLoading);
  useFrontDoorFonts();

  if (isLoading) return <div className="fd-boot" />;
  if (isAuthenticated) return <Navigate to="/home" replace />;

  const teacherSite = teacherSiteUrl();

  return (
    <div className="fd">
      <SiteHeader
        current="hub"
        app="student"
        actions={
          <details className="fd-menu">
            <summary onClick={() => track('cta_click', { cta: 'nav_signin_menu' })}>
              Sign in
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M6 9l6 6 6-6" />
              </svg>
            </summary>
            <ul className="fd-menu-list">
              <li>
                <Link to="/login" onClick={() => track('cta_click', { cta: 'nav_signin_student' })}>
                  Student <span>Homework, tests and practice</span>
                </Link>
              </li>
              <li>
                <a href={`${teacherSite}/login`} onClick={() => track('cta_click', { cta: 'nav_signin_teacher' })}>
                  Teacher <span>Your classes and marking</span>
                </a>
              </li>
            </ul>
          </details>
        }
      />

      <main>
        <section className="fd-hero">
          <div className="fd-wrap">
            <p className="fd-eyebrow">Science &amp; Maths · Classes 8–12</p>
            <h1 className="fd-h1" style={{ maxWidth: 880 }}>
              BuzzBrain reads students’ working and shows where it went wrong.
            </h1>

            <div className="fd-grid fd-doors">
              <div className="fd-card fd-door">
                <h2>I teach</h2>
                <p>Check answer sheets in minutes, and see each student’s mistakes.</p>
                <div className="fd-door-actions">
                  <a
                    href={`${teacherSite}/teachers`}
                    className="fd-btn fd-btn--primary"
                    onClick={() => track('cta_click', { cta: 'door_teacher' })}
                  >
                    For teachers →
                  </a>
                </div>
              </div>

              <div className="fd-card fd-door">
                <h2>I’m a student</h2>
                <p>Practise questions for your class, and get feedback on every step of your working.</p>
                <div className="fd-door-actions">
                  <Link
                    to="/students"
                    className="fd-btn fd-btn--dark"
                    onClick={() => track('cta_click', { cta: 'door_student' })}
                  >
                    For students →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="fd-section">
          <div className="fd-wrap fd-split">
            <div>
              <h2 className="fd-h2">The final answer matches the key. Two steps are wrong.</h2>
              <p className="fd-lead">An answer key would give this full marks. BuzzBrain marks the working, line by line.</p>
            </div>
            <div className="fd-split-wide">
              <WorkingSample />
            </div>
          </div>
        </section>

        <section className="fd-section--band">
          <div className="fd-wrap" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 16px' }}>
            <strong>Covers</strong>
            <CoverageChips />
          </div>
        </section>

        <LabsSection
          title="Labs for Physics and Chemistry"
          lead="Experiments students can run for themselves, and teachers can put up in class."
        />

        <section className="fd-section">
          <div className="fd-wrap">
            <div className="fd-split fd-section--warm" style={{ borderRadius: 20, padding: 36, gap: 32 }}>
              <div>
                <h2 className="fd-h2 fd-h2--sm">Studying on your own?</h2>
                <p className="fd-p" style={{ marginBottom: 20 }}>You don’t need a teacher to start.</p>
                <Link
                  to="/login?register=1"
                  className="fd-btn fd-btn--dark"
                  onClick={() => track('cta_click', { cta: 'self_study_start' })}
                >
                  Start practising free
                </Link>
              </div>
              <ul className="fd-ticks fd-split-wide">
                <li><Tick /><span><strong>Board questions for your class</strong> — Classes 8–12, plus JEE and NEET practice.</span></li>
                <li><Tick /><span><strong>Write your working</strong> on paper or on screen, and get feedback on each step.</span></li>
                <li><Tick /><span><strong>Know what to revise next</strong>, from the mistakes you actually made.</span></li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <footer className="fd-footer">
        <div className="fd-wrap fd-footer-inner">
          <div>
            <div className="fd-footer-brand">BuzzBrain</div>
            <p>Running a school or coaching centre? <a href={SCHOOLS_CONTACT}>Talk to us</a></p>
          </div>
          <nav aria-label="Footer">
            <a href={`${teacherSite}/teachers`}>For teachers</a>
            <Link to="/students">For students</Link>
            <a href={SCHOOLS_CONTACT}>For schools</a>
            <a href="/privacy.html">Privacy</a>
            <a href="/support.html">Support</a>
            <a href="mailto:support@buzzbrain.ai">Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
